/**
 * Side-panel listing every equipment-list item with type filter + click-to-focus.
 * Plain JSX (outside <Canvas> — safe for visual-edits babel plugin).
 *
 * When `showDimensions` is true AND a row is the active selection, the row
 * expands inline with editable N / S / E / W / Roof clearance inputs that
 * re-position the equipment to honour the typed clearance. Each label is
 * colour-coded to match the in-canvas DimensionOverlay.
 */
import { useEffect, useMemo, useState } from "react";
import { X, Target, ArrowsVertical, ArrowsHorizontal } from "@phosphor-icons/react";
import { computeClearances, DIM_COLORS, orientedDimensions } from "@/components/r3d/scene";
import { DEFAULT_AXIS_MAP, flipVerticalAxisMap, flipHorizontalAxisMap } from "@/lib/orientation";

/** v1.65 — Tiny SVG axis-tripod that shows which intrinsic dimension (L/W/D)
 *  is currently aligned with each world axis (X/Y/Z) for the selected
 *  equipment. Drawn in cyan/magenta/yellow so it's instantly readable
 *  alongside the flip buttons. */
function OrientationGizmo({ item }) {
  const { axis_map } = orientedDimensions(item);
  const initial = { length: "L", width: "W", depth: "D" };
  const colorFor = { length: "#FFCC00", width: "#FF8A85", depth: "#00E5FF" };
  return (
    <svg width="58" height="58" viewBox="0 0 58 58" className="shrink-0"
         data-testid={`r3d-orient-gizmo-${item.id}`}
         aria-label="Orientation gizmo — shows which intrinsic dimension maps to each world axis">
      {/* Origin point */}
      <circle cx="18" cy="40" r="2" fill="#888" />
      {/* X axis (right) — uses axis_map[0] */}
      <g>
        <line x1="18" y1="40" x2="50" y2="40" stroke={colorFor[axis_map[0]]} strokeWidth="1.5" />
        <polygon points="50,40 45,37 45,43" fill={colorFor[axis_map[0]]} />
        <text x="52" y="44" fontSize="9" fontFamily="monospace" fill={colorFor[axis_map[0]]} fontWeight="bold">
          {initial[axis_map[0]]}
        </text>
        <text x="40" y="52" fontSize="7" fontFamily="monospace" fill="#888">X</text>
      </g>
      {/* Y axis (up) — uses axis_map[1] */}
      <g>
        <line x1="18" y1="40" x2="18" y2="10" stroke={colorFor[axis_map[1]]} strokeWidth="1.5" />
        <polygon points="18,10 15,15 21,15" fill={colorFor[axis_map[1]]} />
        <text x="6" y="13" fontSize="9" fontFamily="monospace" fill={colorFor[axis_map[1]]} fontWeight="bold">
          {initial[axis_map[1]]}
        </text>
        <text x="22" y="14" fontSize="7" fontFamily="monospace" fill="#888">Y</text>
      </g>
      {/* Z axis (forward, drawn as diagonal back-right) — uses axis_map[2] */}
      <g>
        <line x1="18" y1="40" x2="38" y2="20" stroke={colorFor[axis_map[2]]} strokeWidth="1.5" strokeDasharray="2 2" />
        <polygon points="38,20 32,21 36,26" fill={colorFor[axis_map[2]]} />
        <text x="40" y="22" fontSize="9" fontFamily="monospace" fill={colorFor[axis_map[2]]} fontWeight="bold">
          {initial[axis_map[2]]}
        </text>
        <text x="28" y="32" fontSize="7" fontFamily="monospace" fill="#888">Z</text>
      </g>
    </svg>
  );
}

/** Quick-orient presets identical to the 2D ElevationViews panel so the user
 *  doesn't have to switch tabs to change a panel's mounting orientation.
 *  Orientation math identical to elevation/EquipmentEditPanel.jsx QUICK_PRESETS. */
const R3D_ORIENT_PRESETS = [
  { id: "wall-tall",      label: "Wall · upright (tall)",       patch: { rotation_deg: 90, orientation_flip: true  }, mountElev: null },
  { id: "wall-landscape", label: "Wall · landscape",            patch: { rotation_deg: 0,  orientation_flip: true  }, mountElev: null },
  { id: "floor-flat",     label: "Floor · lay flat (face up)",  patch: { rotation_deg: 0,  orientation_flip: false }, mountElev: 0    },
  { id: "ceiling-flat",   label: "Ceiling · face down",         patch: { rotation_deg: 0,  orientation_flip: false }, mountElev: 88   },
  { id: "wall-back",      label: "Wall · back→wall (legacy)",   patch: { rotation_deg: 90, orientation_flip: true  }, mountElev: null },
];

/** Inline orientation editor — rotation buttons + flip toggle + preset menu.
 *  Mirrors the 2D ElevationViews EquipmentEditPanel so users can change
 *  orientation directly from the 3D scene. */
function OrientationEditor({ item, items, room, update }) {
  const [presetOpen, setPresetOpen] = useState(false);
  const rotDeg = ((Number(item.rotation_deg) || 0) % 360 + 360) % 360;
  const isFlipped = !!item.orientation_flip;

  const apply = (patch, mountOverride) => {
    const next = (items || []).map((it) => {
      if (it.id !== item.id) return it;
      const updated = { ...it, ...patch };
      // Compute the NEW oriented bounding box after the patch is applied,
      // then clamp position + mount-height so the box stays inside the room
      // (fixes the v1.60 "PNL-002 sticks out of enclosure after flip" bug).
      const { wallSize, vertSize, thickness } = orientedDimensions(updated);
      const rL = room?.length_in || 240;
      const rW = room?.width_in  || 180;
      const rH = room?.depth_in  || 96;
      const pos = updated.position || {};
      const newX = Math.max(0, Math.min(rL - wallSize,   Number(pos.x_in) || 0));
      const newY = Math.max(0, Math.min(rW - thickness, Number(pos.y_in) || 0));
      // v1.71 — sync pos.w_in/h_in IMMEDIATELY (don't wait for the backend
      // normalize on autosave). Otherwise local consumers like the 2D
      // PlanDimensions counter, SizeTab clamp, and CSV/BOM dumps see a
      // stale footprint between flip and the 1.5 s debounced save.
      updated.position = { ...pos, x_in: newX, y_in: newY,
                           w_in: wallSize, h_in: thickness };
      const mountTarget = (mountOverride != null)
        ? mountOverride
        : Math.max(0, Number(updated.mount_height_in) || 0);
      updated.mount_height_in = Math.max(0, Math.min(rH - vertSize, mountTarget));
      return updated;
    });
    update({ equipment_list: next });
  };
  const setRotation = (deg) => apply({ rotation_deg: deg });
  /** v1.64 — Flip Vertical: rotate the box 90° about its current world-X
   *  axis. Computes the new axis_map and clears the legacy rotation_deg /
   *  orientation_flip so the two systems don't drift. */
  const flipVertical = () => {
    const newAm = flipVerticalAxisMap(item);
    apply({ axis_map: newAm, rotation_deg: 0, orientation_flip: false });
  };
  /** v1.64 — Flip Horizontal: rotate 90° about world-Y axis. */
  const flipHorizontal = () => {
    const newAm = flipHorizontalAxisMap(item);
    apply({ axis_map: newAm, rotation_deg: 0, orientation_flip: false });
  };
  /** v1.66 — Reset Orientation: restores the default axis_map
   *  ['length','depth','width'] in one click and clears the legacy
   *  rotation_deg / orientation_flip flags. Position auto-clamps as usual. */
  const resetOrientation = () => {
    apply({
      axis_map: DEFAULT_AXIS_MAP.slice(),
      rotation_deg: 0,
      orientation_flip: false,
    });
  };
  /** v1.67 — Align to Wall: set the axis_map so the panel's thickness
   *  ("depth") points PERPENDICULAR to the nearest wall, and snap the
   *  position so the back face touches that wall. Resolves the recurring
   *  "panel placed on wall B but oriented as if on wall A" mismatch seen
   *  in magbade2013's project (PNL-002). */
  const alignToWall = () => {
    if (!room || !item.position) return;
    const p = item.position;
    const rL = room.length_in || 240;
    const rW = room.width_in  || 180;
    // Use the current oriented bbox to compute the center for wall-detection,
    // even though we're about to overwrite the axis_map.
    const cur = orientedDimensions(item);
    const cx = p.x_in + cur.wallSize  / 2;
    const cz = p.y_in + cur.thickness / 2;
    const dW = cx, dE = rL - cx, dN = cz, dS = rW - cz;
    const nearest = Math.min(dW, dE, dN, dS);
    const wallIsX = (nearest === dW || nearest === dE);
    // New axis_map: depth = perpendicular-to-wall axis; length = vertical.
    // Width = along-wall axis.
    const newAm = wallIsX
      ? ["depth",  "length", "width"]   // perpendicular = X (wall D/B)
      : ["width",  "length", "depth"];  // perpendicular = Z (wall A/C)
    // New post-align dimensions:
    const tempItem = { ...item, axis_map: newAm };
    const newOd = orientedDimensions(tempItem);
    // Snap the panel flush to the nearest wall.
    let newX = p.x_in, newY = p.y_in;
    if      (nearest === dW) newX = 0;
    else if (nearest === dE) newX = Math.max(0, rL - newOd.wallSize);
    else if (nearest === dN) newY = 0;
    else if (nearest === dS) newY = Math.max(0, rW - newOd.thickness);
    // Clamp the other axis so the box still fits.
    newX = Math.max(0, Math.min(rL - newOd.wallSize,  newX));
    newY = Math.max(0, Math.min(rW - newOd.thickness, newY));
    const next = (items || []).map((it) => {
      if (it.id !== item.id) return it;
      return {
        ...it,
        axis_map: newAm,
        rotation_deg: 0,
        orientation_flip: false,
        position: { ...it.position, x_in: newX, y_in: newY },
      };
    });
    update({ equipment_list: next });
  };
  const applyPreset = (preset) => {
    apply(preset.patch, preset.mountElev);
    setPresetOpen(false);
  };

  return (
    <div className="mt-2 px-3 pb-3 font-mono text-[10px]" data-testid={`r3d-equip-orient-${item.id}`}>
      <div className="label-tag mb-1 text-[9px]">Orientation</div>
      <div className="flex items-start gap-3 flex-wrap">
        {/* v1.65 — tiny axis-tripod legend so the user can see at a glance
            which intrinsic dimension (L = length, W = width, D = depth)
            currently maps to each world axis after a sequence of flips. */}
        <OrientationGizmo item={item} />
        <div className="flex items-center gap-2 flex-wrap flex-1">
        {/* v1.64 — Flip Vertical & Flip Horizontal replace the single Flip
            button. Each rotates 90° from the current pose (not the original)
            so they compose; combined they reach all 6 cube orientations. */}
        <button type="button" onClick={flipVertical}
                className="inline-flex items-center gap-1 px-2 py-0.5 border border-white/15 text-gray-300 hover:border-[#FFCC00] hover:text-[#FFCC00]"
                title="Rotate the box 90° about its horizontal (X) axis. Vertical ↔ thickness swap; position auto-clamps to room."
                data-testid="r3d-orient-flip-vertical">
          <ArrowsVertical size={10} weight="bold" />
          Flip ↕
        </button>
        <button type="button" onClick={flipHorizontal}
                className="inline-flex items-center gap-1 px-2 py-0.5 border border-white/15 text-gray-300 hover:border-[#FFCC00] hover:text-[#FFCC00]"
                title="Rotate the box 90° about its vertical (Y) axis. Wall-parallel ↔ thickness swap; position auto-clamps to room."
                data-testid="r3d-orient-flip-horizontal">
          <ArrowsHorizontal size={10} weight="bold" />
          Flip ↔
        </button>
        {/* v1.66 — Reset Orientation restores the default axis_map in one
            click (handy after composing 3-4 flips and wanting to start over). */}
        <button type="button" onClick={resetOrientation}
                className="inline-flex items-center gap-1 px-2 py-0.5 border border-white/15 text-gray-400 hover:border-[#00E5FF] hover:text-[#00E5FF]"
                title="Reset orientation to the default pose (length→X, depth→Y, width→Z). Clears any composed flips."
                data-testid="r3d-orient-reset">
          ⟲ Reset
        </button>
        {/* v1.67 — Align to Wall — set axis_map so thickness is perpendicular
            to the nearest wall AND snap the panel flush against it. Resolves
            the recurring "2D doesn't match 3D" complaint when a panel is
            positioned on one wall but oriented for a different wall. */}
        <button type="button" onClick={alignToWall}
                className="inline-flex items-center gap-1 px-2 py-0.5 border border-[#00E5FF]/40 text-[#00E5FF] hover:bg-[#00E5FF]/10"
                title="Auto-orient the panel so its thickness (depth) is perpendicular to the nearest wall, then snap it flush. Resolves orientation/position mismatches in one click."
                data-testid="r3d-orient-align-wall">
          ◧ Align to wall
        </button>
        <div className="relative">
          <button type="button"
                  onClick={() => setPresetOpen((v) => !v)}
                  className="inline-flex items-center gap-1 px-2 py-0.5 border border-[#00E5FF]/40 text-[#00E5FF] hover:bg-[#00E5FF]/10"
                  data-testid="r3d-orient-preset-toggle">
            Presets ▾
          </button>
          {presetOpen && (
            <div className="absolute z-30 top-full mt-1 right-0 bg-[#050608] border border-[#00E5FF]/40 shadow-xl w-[220px] py-1"
                 data-testid="r3d-orient-preset-menu">
              {R3D_ORIENT_PRESETS.map((preset) => (
                <button key={preset.id} type="button"
                        onClick={() => applyPreset(preset)}
                        className="w-full px-2 py-1 text-left hover:bg-[#00E5FF]/10 text-gray-200"
                        data-testid={`r3d-orient-preset-${preset.id}`}>
                  {preset.label}
                </button>
              ))}
            </div>
          )}
        </div>
        </div>
      </div>
    </div>
  );
}

function DistanceEditor({ item, room, allItems, update }) {
  const cl = useMemo(
    () => computeClearances(item, room, (allItems || []).filter((i) => i.position)),
    [item, room, allItems],
  );
  if (!cl) return null;
  const p = item.position;
  // v1.65 — use orientedDimensions so the W/E/N/S/ROOF math reflects the
  // POST-flip bounding box. Pre-v1.65 the editor used the stored
  // pos.w_in/pos.h_in directly, which are STALE after a flip → typing
  // East = 0 silently put the box at (room.length_in − stale_w) instead of
  // (room.length_in − actual_wallSize), and the bug compounded as the user
  // dragged the box: clearance values jumped sporadically.
  const _odItem = orientedDimensions(item);
  const w = _odItem.wallSize;
  const d = _odItem.thickness;
  const h_eq = _odItem.vertSize;

  // Apply a new clearance. If the closest target on this side is a NEIGHBOR
  // (another equipment) we anchor to the neighbor's facing edge — typing X
  // means "put X inches of clearance to that neighbor". Otherwise we anchor
  // to the wall (typing X = X inches from the wall).
  const apply = (side, newDistRaw) => {
    const newDist = Math.max(0, Number(newDistRaw) || 0);
    const info = cl[side];
    const isNeighbor = info && info.kind === "neighbor";
    // Find the neighbor object (if any) on this side by re-scanning.
    const findNeighbor = () => {
      const p0 = item.position; if (!p0) return null;
      let best = null;
      for (const ot of (allItems || [])) {
        if (!ot.position || ot.id === item.id) continue;
        const op = ot.position;
        // v1.65 — neighbor sizing through orientedDimensions too.
        const otOd = orientedDimensions(ot);
        const ow = otOd.wallSize, od = otOd.thickness;
        const aw = w, ad = d;
        const xOverlap = !(op.x_in + ow < p0.x_in || op.x_in > p0.x_in + aw);
        const yOverlap = !(op.y_in + od < p0.y_in || op.y_in > p0.y_in + ad);
        let dist = null;
        if (side === "W" && yOverlap && op.x_in + ow <= p0.x_in)  dist = p0.x_in - (op.x_in + ow);
        if (side === "E" && yOverlap && op.x_in >= p0.x_in + aw)  dist = op.x_in - (p0.x_in + aw);
        if (side === "N" && xOverlap && op.y_in + od <= p0.y_in)  dist = p0.y_in - (op.y_in + od);
        if (side === "S" && xOverlap && op.y_in >= p0.y_in + ad)  dist = op.y_in - (p0.y_in + ad);
        if (dist != null && (best == null || dist < best.dist)) best = { dist, item: ot };
      }
      return best;
    };
    const nb = isNeighbor ? findNeighbor() : null;
    const next = (allItems || []).map((it) => {
      if (it.id !== item.id || !it.position) return it;
      const np = { ...it.position };
      if (nb) {
        const op = nb.item.position;
        const nbOd = orientedDimensions(nb.item);
        const ow = nbOd.wallSize, od = nbOd.thickness;
        if (side === "W")      np.x_in = op.x_in + ow + newDist;
        else if (side === "E") np.x_in = op.x_in - w - newDist;
        else if (side === "N") np.y_in = op.y_in + od + newDist;
        else if (side === "S") np.y_in = op.y_in - d - newDist;
      } else {
        if (side === "W") np.x_in = newDist;
        else if (side === "E") np.x_in = Math.max(0, room.length_in - w - newDist);
        else if (side === "N") np.y_in = newDist;
        else if (side === "S") np.y_in = Math.max(0, room.width_in - d - newDist);
      }
      // Clamp to room interior using POST-orientation dimensions.
      np.x_in = Math.max(0, Math.min(room.length_in - w, np.x_in));
      np.y_in = Math.max(0, Math.min(room.width_in  - d, np.y_in));
      return { ...it, position: np };
    });
    update({ equipment_list: next });
  };

  // Apply a NEW mount height (FLOOR clearance = mount_height_in) or roof
  // clearance. Roof_clearance + mount_height + equipment_height = room.depth_in.
  // Editing either side updates mount_height_in directly.
  const applyVertical = (side, valueRaw) => {
    const value = Math.max(0, Number(valueRaw) || 0);
    const ceiling = room.depth_in || 96;
    let newMount;
    if (side === "FLOOR") {
      newMount = value;
    } else {
      // ROOF: distance from top-of-equipment to ceiling = value
      newMount = Math.max(0, ceiling - h_eq - value);
    }
    // Clamp so equipment never pokes through ceiling
    newMount = Math.min(newMount, Math.max(0, ceiling - h_eq));
    const next = (allItems || []).map((it) =>
      it.id === item.id ? { ...it, mount_height_in: Math.round(newMount * 10) / 10 } : it,
    );
    update({ equipment_list: next });
  };

  const rows = [
    { key: "W", label: "← W", info: cl.W,    isVertical: false },
    { key: "E", label: "E →", info: cl.E,    isVertical: false },
    { key: "N", label: "↑ N", info: cl.N,    isVertical: false },
    { key: "S", label: "↓ S", info: cl.S,    isVertical: false },
    { key: "FLOOR", label: "⌄ Floor", info: { dist: Math.max(0, Number(item.mount_height_in) || 0), target: "floor" }, isVertical: true },
    { key: "ROOF",  label: "⌃ Roof",  info: cl.ROOF, isVertical: true },
  ];

  return (
    <div className="mt-2 grid grid-cols-2 gap-1 px-3 pb-3" data-testid="r3d-equip-dim-editor">
      {rows.map((r) => (
        <div key={r.key} className="flex items-center gap-1 text-[10px] font-mono">
          <span
            className="px-1.5 py-0.5 font-bold whitespace-nowrap"
            style={{ background: DIM_COLORS[r.key] || "#888", color: "#0A0C10" }}
          >
            {r.label}
          </span>
          <input
            type="number"
            min={0}
            value={Math.round(r.info.dist)}
            onChange={(e) => (r.isVertical ? applyVertical(r.key, e.target.value) : apply(r.key, e.target.value))}
            className="w-12 bg-black/40 border border-white/10 px-1 py-0.5 text-center text-gray-200 focus:border-[#00E5FF] outline-none"
            data-testid={`r3d-dim-${r.key}`}
          />
          <span className="text-gray-500 truncate">→ {r.info.target}</span>
        </div>
      ))}
      <div className="col-span-2 text-[9px] text-gray-500 mt-1">
        Edit any value to snap-position. <b className="text-gray-400">Floor</b> = bottom-of-equipment above the floor (wall/ceiling mount).
        Roof clearance = ceiling − top-of-equipment. Editing one updates the other automatically.
      </div>
      {/* v1.81 — Mounting bracket / stand-off depth. When > 0, the
          equipment is depicted as sitting on a bracket of this depth
          rather than flush against the wall/floor. Auto-detected closest
          wall/floor determines bracket orientation in 2D plan + 3D. */}
      <div className="col-span-2 flex items-center gap-2 mt-1">
        <span className="px-1.5 py-0.5 font-bold whitespace-nowrap"
              style={{ background: "#FF9F0A", color: "#0A0C10" }}>⊏ Mnt depth</span>
        <input type="number" min={0}
               value={Math.max(0, Number(item.mounting_depth_in) || 0)}
               onChange={(e) => {
                 const v = Math.max(0, Number(e.target.value) || 0);
                 const next = (allItems || []).map((it) =>
                   it.id === item.id ? { ...it, mounting_depth_in: Math.round(v * 10) / 10 } : it,
                 );
                 update({ equipment_list: next });
               }}
               className="w-16 bg-black/40 border border-white/10 px-1.5 py-0.5 text-gray-200 focus:border-[#FF9F0A] outline-none"
               data-testid={`r3d-mounting-depth-${item.id}`} />
        <span className="text-gray-500 text-[9px] flex-1">
          Bracket / stand-off (in). 0 = flush mount; &gt; 0 = pushed off wall/floor by this much.
        </span>
      </div>
      {/* v1.95 — Installation side. Interior (default), Exterior (on the
          OUTSIDE face of the closest wall — e.g. lights, receptacles, HVAC
          condenser), or Through (straddles the wall — e.g. through-wall
          HVAC, PT/CT bay). */}
      <div className="col-span-2 flex items-center gap-2 mt-1">
        <span className="px-1.5 py-0.5 font-bold whitespace-nowrap"
              style={{ background: "#00E5FF", color: "#0A0C10" }}>⌂ Install</span>
        <select value={item.installation_side || "interior"}
                onChange={(e) => {
                  const v = e.target.value;
                  const next = (allItems || []).map((it) =>
                    it.id === item.id ? { ...it, installation_side: v } : it,
                  );
                  update({ equipment_list: next });
                }}
                className="bg-black/40 border border-white/10 px-1.5 py-0.5 text-gray-200 focus:border-[#00E5FF] outline-none"
                data-testid={`r3d-install-side-${item.id}`}>
          <option value="interior">Interior (inside enclosure)</option>
          <option value="exterior">Exterior (outside wall face)</option>
          <option value="through">Through (straddles wall)</option>
        </select>
      </div>
    </div>
  );
}

export default function EquipmentSidePanel({
  items, onFocus, onClose, selected, selectedSet,
  showDimensions = false, room = null, update,
  trays = [], selectedTrayId = null, setSelectedTrayId,
  onTrayRename, onTrayDelete, onTrayFocus, onTrayRotate,
}) {
  const [filter, setFilter] = useState("all");
  const [section, setSection] = useState("equipment");  // equipment | trays
  const types = useMemo(() => {
    const s = new Set();
    items.forEach((i) => s.add((i.equipment_type || "other").toLowerCase()));
    return ["all", ...Array.from(s).sort()];
  }, [items]);
  const filtered = filter === "all"
    ? items
    : items.filter((i) => (i.equipment_type || "other").toLowerCase() === filter);

  return (
    <div
      className="absolute top-0 right-0 h-full w-80 bg-[#080a0d]/95 backdrop-blur border-l border-white/10 z-10 flex flex-col"
      data-testid="r3d-side-panel"
    >
      <div className="flex items-center justify-between p-3 border-b border-white/10">
        <div className="flex gap-1">
          <button
            onClick={() => setSection("equipment")}
            className={`label-tag px-2 py-1 ${section === "equipment" ? "!text-[#00E5FF]" : ""}`}
            data-testid="r3d-tab-equipment"
          >// EQUIPMENT ({items.length})</button>
          <button
            onClick={() => setSection("trays")}
            className={`label-tag px-2 py-1 ${section === "trays" ? "!text-[#FFCC00]" : ""}`}
            data-testid="r3d-tab-trays"
          >// TRAYS ({trays.length})</button>
        </div>
        <button onClick={onClose} className="text-gray-400 hover:text-white" data-testid="r3d-side-panel-close">
          <X size={16} />
        </button>
      </div>

      {section === "trays" ? (
        <>
          {/* Detail editor for the currently selected tray */}
          <TrayDetailEditor
            tray={trays.find((t) => t.id === selectedTrayId) || null}
            room={room}
            onRename={onTrayRename}
            onChange={(patch) => {
              if (!selectedTrayId) return;
              const next = trays.map((t) => (t.id === selectedTrayId ? { ...t, ...patch } : t));
              update && update({ cable_trays: next });
            }}
          />
          <ul className="overflow-auto flex-1 divide-y divide-white/5" data-testid="r3d-tray-list">
          {trays.map((t, i) => {
            const idLabel = t.id_display || t.tag || t.id || `CT-${String(i + 1).padStart(3, "0")}`;
            const isSel = selectedTrayId === t.id;
            const [x1, y1] = t.points?.[0] || [];
            const [x2, y2] = t.points?.[1] || [];
            const isVert = Math.abs((x2 || 0) - (x1 || 0)) < 0.5 && Math.abs((y2 || 0) - (y1 || 0)) < 0.5;
            return (
              <li key={t.id} className={`px-3 py-2 font-mono text-xs ${isSel ? "bg-[#FFCC00]/10" : ""}`} data-testid={`r3d-tray-item-${idLabel}`}>
                <div className="flex items-center gap-2">
                  <button onClick={() => setSelectedTrayId && setSelectedTrayId(t.id)} className="flex-1 text-left">
                    <div className={`font-bold ${isSel ? "text-[#FFCC00]" : "text-gray-300"}`}>{idLabel}</div>
                    <div className="text-[10px] text-gray-500">
                      {t.kind === "fitting"
                        ? `fitting · ${t.fitting_type || ""}`.trim()
                        : isVert ? "vertical run"
                        : `${t.kind || "run"} · (${(x1 || 0).toFixed(0)},${(y1 || 0).toFixed(0)})→(${(x2 || 0).toFixed(0)},${(y2 || 0).toFixed(0)})`}
                    </div>
                  </button>
                  <button
                    onClick={() => onTrayFocus && onTrayFocus(t)}
                    className="text-gray-500 hover:text-[#00E5FF]"
                    title="Fly camera to this tray"
                    data-testid={`r3d-tray-focus-${t.id}`}
                  >⌖</button>
                  {t.kind !== "fitting" && (
                    <button
                      onClick={() => onTrayRotate && onTrayRotate(t.id)}
                      className={`text-gray-500 hover:text-[#FFCC00] ${
                        Number(t.rotation_axis_deg) === 90 ? "!text-[#FFCC00]" : ""
                      }`}
                      title="Rotate 90° about centerline"
                      data-testid={`r3d-tray-rotate-${t.id}`}
                    >↻</button>
                  )}
                  <button
                    onClick={() => onTrayDelete && onTrayDelete(t.id)}
                    className="text-gray-500 hover:text-red-400"
                    title="Delete"
                    data-testid={`r3d-tray-del-${t.id}`}
                  >×</button>
                </div>
              </li>
            );
          })}
          {trays.length === 0 && (
            <li className="p-4 text-xs text-gray-500 font-mono">
              No trays yet. Add segments on the Size tab or use the Tray Designer.
            </li>
          )}
        </ul>
        </>
      ) : (
      <>
      <div className="p-3 border-b border-white/10 space-y-2">
        <div>
          <div className="label-tag mb-1 text-[9px]">Filter by type</div>
          <select
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className="w-full bg-black/40 border border-white/10 px-2 py-1.5 text-xs font-mono focus:border-[#00E5FF] outline-none"
            data-testid="r3d-type-filter"
          >
            {types.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
        </div>
        <div className="text-[10px] text-gray-500 font-mono leading-snug">
          Click to select · <span className="text-gray-300">Shift+click</span> for Isolate.
          {showDimensions && <> · Selected item shows colour-coded clearances below.</>}
        </div>
      </div>
      <ul className="overflow-auto flex-1 divide-y divide-white/5" data-testid="r3d-equip-list">
        {filtered.map((it) => {
          const placed = !!it.position && it.position.x_in !== undefined && it.position.x_in >= 0;
          const inSet = selectedSet?.has(it.id);
          const isSelected = selected === it.id;
          return (
            <li key={it.id}>
              <button
                onClick={(e) => onFocus(it, e.shiftKey)}
                className={`w-full text-left px-3 py-2 font-mono text-xs flex items-center gap-2 hover:bg-white/5 ${isSelected ? "bg-[#00E5FF]/10 text-[#00E5FF]" : "text-gray-300"} ${inSet ? "border-l-2 border-[#FFCC00]" : "border-l-2 border-transparent"}`}
                data-testid={`r3d-equip-item-${it.id}`}
                data-tag={it.tag || ""}
                data-equip-row={it.tag || it.id}
              >
                <Target size={12} className="shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="truncate font-bold">{it.tag || "—"}</div>
                  <div className="text-[10px] text-gray-500 truncate">
                    {it.equipment_type || "—"} · {Math.round(it.length_in || 0)}×{Math.round(it.width_in || 0)}×{Math.round(it.depth_in || 0)}&quot;
                  </div>
                </div>
                <span className={`text-[9px] uppercase tracking-wider px-1 ${placed ? "text-cyan-400" : "text-gray-500"}`}>
                  {placed ? "placed" : "unplaced"}
                </span>
              </button>
              {isSelected && placed && room && update && (
                <OrientationEditor item={it} items={items} room={room} update={update} />
              )}
              {isSelected && showDimensions && placed && room && update && (
                <DistanceEditor item={it} room={room} allItems={items} update={update} />
              )}
            </li>
          );
        })}
        {filtered.length === 0 && (
          <li className="p-4 text-xs text-gray-500 font-mono">No equipment in this category.</li>
        )}
      </ul>
      </>
      )}
    </div>
  );
}


/* ─── Tray detail editor (End A / End B distances + ID rename) ────── */

/** Default elevations used when a tray has no explicit values yet. */
const DEFAULT_RUN_ELEVATION_FROM_CEILING_IN = 12;
const DEFAULT_DROP_BOTTOM_IN = 78;

/** Identify the wall the tray is anchored to (closest of A/B/C/D), and the
 *  two perpendicular cap walls.
 *  Walls: A=back(y=W), B=right(x=L), C=front(y=0), D=left(x=0).
 *  - If anchored to A or C (top/bottom horizontal walls), perpendicular = B & D
 *  - If anchored to B or D (left/right walls),               perpendicular = A & C
 */
function anchorAndPerpendicular({ x, y, L, W }) {
  const d = {
    A: Math.max(0, W - y),
    B: Math.max(0, L - x),
    C: Math.max(0, y),
    D: Math.max(0, x),
  };
  const anchor = ["A", "B", "C", "D"].sort((p, q) => d[p] - d[q])[0];
  const perps = (anchor === "A" || anchor === "C") ? ["B", "D"] : ["A", "C"];
  return { anchor, perps, d };
}
/** Distance from a point (x, y) to a named wall in the room. */
function distToWall(wall, { x, y, L, W }) {
  switch (wall) {
    case "A": return Math.max(0, W - y); // back
    case "B": return Math.max(0, L - x); // right
    case "C": return Math.max(0, y);     // front
    case "D": return Math.max(0, x);     // left
    default:  return 0;
  }
}
/** Move (x, y) so its distance to the named `wall` equals `newDist`. */
function moveTowardWall(wall, { x, y, L, W }, newDist) {
  const d = Math.max(0, Math.min(newDist, wall === "A" || wall === "C" ? W : L));
  switch (wall) {
    case "A": return [x, Math.max(0, Math.min(W, W - d))];
    case "B": return [Math.max(0, Math.min(L, L - d)), y];
    case "C": return [x, Math.max(0, Math.min(W, d))];
    case "D": return [Math.max(0, Math.min(L, d)), y];
    default:  return [x, y];
  }
}

/** Tiny number input row used inside the editor. */
function NumRow({ label, value, color, onChange, disabled, testid }) {
  return (
    <label className="flex items-center justify-between gap-2 text-[11px]" data-testid={testid}>
      <span className="text-gray-400 truncate" style={{ color }}>{label}</span>
      <input
        type="number"
        min="0"
        step="1"
        value={Number.isFinite(value) ? Math.round(value) : ""}
        onChange={(e) => onChange(e.target.value === "" ? null : Number(e.target.value))}
        disabled={disabled}
        className="bg-black/40 border border-white/10 px-2 py-1 font-mono text-[11px] w-20 text-right focus:border-[#FFCC00] outline-none disabled:opacity-40"
      />
    </label>
  );
}

function TrayDetailEditor({ tray, room, onRename, onChange }) {
  const [editingId, setEditingId] = useState(false);
  const [idDraft, setIdDraft]     = useState("");
  // Reset draft whenever the selected tray changes.
  useEffect(() => {
    setEditingId(false);
    setIdDraft(tray?.id_display || "");
  }, [tray?.id]);

  if (!tray) return null;

  const L = room?.length_in || 240;
  const W = room?.width_in  || 180;
  const H = room?.depth_in  || 96;

  const idLabel = tray.id_display || tray.tag || (tray.id ? `CT-${tray.id.slice(0, 6)}` : "TRAY");
  const isDrop = tray.kind === "drop";
  const [[x1, y1], [x2, y2]] = [tray.points?.[0] || [0, 0], tray.points?.[1] || [0, 0]];

  // Elevation model:
  //   horizontal run:  elevation_in (single value, shared by both ends)
  //   vertical drop:   top_in + bottom_in (per end)
  const elevation_in = tray.elevation_in ?? Math.max(0, H - DEFAULT_RUN_ELEVATION_FROM_CEILING_IN);
  const top_in       = tray.top_in       ?? Math.max(0, H - DEFAULT_RUN_ELEVATION_FROM_CEILING_IN);
  const bottom_in    = tray.bottom_in    ?? DEFAULT_DROP_BOTTOM_IN;

  // Direct X/Y coordinate model (distance from Wall D = x, distance from
  // Wall C = y). Lets the user move endpoints freely in both axes without
  // the perp-wall axis lock that previously flipped as the tray crossed
  // the room midpoint (which felt like a 90° rotation).
  const anchorInfo = anchorAndPerpendicular({ x: x1, y: y1, L, W });
  const floorA = isDrop ? top_in    : elevation_in;
  const floorB = isDrop ? bottom_in : elevation_in;
  const roofA  = Math.max(0, H - floorA);
  const roofB  = Math.max(0, H - floorB);

  /** Set a single endpoint's X or Y coordinate (in inches from Wall D / C).
   *  Drops: both endpoints move together (drop stays vertical). Clamps with
   *  half-width margin so the tray cross-section never pokes through a wall. */
  const setEndCoord = (which, axis, value) => {
    if (value == null || !Number.isFinite(value)) return;
    const margin = Math.max(0, (Number(tray?.width_in) || 12) / 2);
    const maxAxis = axis === "x" ? L : W;
    const v = Math.max(margin, Math.min(maxAxis - margin, Number(value)));
    const pts = tray.points.map((p) => p.slice());
    const writePoint = (idx) => {
      if (axis === "x") pts[idx][0] = v;
      else              pts[idx][1] = v;
    };
    if (isDrop) {
      writePoint(0); pts[1] = pts[0].slice();
    } else {
      writePoint(which === "A" ? 0 : 1);
    }
    onChange({ points: pts });
  };

  const setFloor = (which, newDist) => {
    if (newDist == null || newDist < 0 || newDist > H) return;
    if (isDrop) {
      // Per-end: A → top, B → bottom.
      onChange(which === "A" ? { top_in: newDist } : { bottom_in: newDist });
    } else {
      // Horizontal run: shared elevation.
      onChange({ elevation_in: newDist });
    }
  };

  const setRoof = (which, newDist) => {
    // Roof distance = H - floor distance.
    setFloor(which, Math.max(0, H - newDist));
  };

  return (
    <div className="border-b border-white/10 bg-[#0a0d12] p-3" data-testid="r3d-tray-detail">
      <div className="flex items-center justify-between mb-2">
        <div className="label-tag !text-[#FFCC00]">// Selected · {isDrop ? "vertical drop" : "horizontal run"}</div>
        {!editingId && (
          <button
            onClick={() => { setIdDraft(tray.id_display || ""); setEditingId(true); }}
            className="text-[10px] text-gray-400 hover:text-[#FFCC00]"
            data-testid="r3d-tray-detail-rename"
          >✎ rename</button>
        )}
      </div>
      {editingId ? (
        <div className="flex gap-1 mb-2" data-testid="r3d-tray-detail-id-edit">
          <input
            autoFocus
            type="text"
            value={idDraft}
            onChange={(e) => setIdDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                const v = idDraft.trim();
                if (v && onRename) onRename(tray.id, v);
                setEditingId(false);
              } else if (e.key === "Escape") {
                setEditingId(false);
              }
            }}
            className="flex-1 bg-black/40 border border-[#FFCC00]/60 px-2 py-1 font-mono text-[13px] focus:outline-none"
            data-testid="r3d-tray-detail-id-input"
          />
          <button
            onClick={() => {
              const v = idDraft.trim();
              if (v && onRename) onRename(tray.id, v);
              setEditingId(false);
            }}
            className="text-[10px] text-[#22c55e] hover:text-white px-2"
            data-testid="r3d-tray-detail-id-save"
          >✓</button>
          <button
            onClick={() => setEditingId(false)}
            className="text-[10px] text-gray-500 hover:text-white px-2"
            data-testid="r3d-tray-detail-id-cancel"
          >✗</button>
        </div>
      ) : (
        <div className="text-[13px] font-display font-bold text-white mb-2" data-testid="r3d-tray-detail-id">{idLabel}</div>
      )}

      {/* End A column / End B column.
          Each end shows its X (from Wall D) and Y (from Wall C) position
          directly so the user can move endpoints in ANY direction without
          the panel's axis swapping near a wall. Drops keep X/Y coincident. */}
      <div className="grid grid-cols-2 gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-widest font-mono mb-1" style={{ color: "#a3e635" }}>End A</div>
          <div className="space-y-1">
            <NumRow label="X in" value={x1} color="#7CFFB2"
                    onChange={(v) => setEndCoord("A", "x", v)} testid="tray-A-x" />
            <NumRow label="Y in" value={y1} color="#7CFFB2"
                    onChange={(v) => setEndCoord("A", "y", v)} testid="tray-A-y" />
            <NumRow label="Floor in"  value={floorA} color="#FFA66B"
                    onChange={(v) => setFloor("A", v)}      testid="tray-A-floor" />
            <NumRow label="Roof in"   value={roofA}  color="#B197FC"
                    onChange={(v) => setRoof("A", v)}       testid="tray-A-roof" />
          </div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-widest font-mono mb-1" style={{ color: "#FF66E0" }}>End B</div>
          <div className="space-y-1">
            <NumRow label="X in" value={x2} color="#7CFFB2"
                    onChange={(v) => setEndCoord("B", "x", v)}
                    disabled={isDrop} testid="tray-B-x" />
            <NumRow label="Y in" value={y2} color="#7CFFB2"
                    onChange={(v) => setEndCoord("B", "y", v)}
                    disabled={isDrop} testid="tray-B-y" />
            <NumRow label="Floor in"  value={floorB} color="#FFA66B"
                    onChange={(v) => setFloor("B", v)}
                    testid="tray-B-floor" />
            <NumRow label="Roof in"   value={roofB}  color="#B197FC"
                    onChange={(v) => setRoof("B", v)}
                    testid="tray-B-roof" />
          </div>
        </div>
      </div>
      <div className="text-[9px] text-gray-500 font-mono mt-2 leading-snug">
        Tray is anchored near Wall {anchorInfo.anchor}. {isDrop
          ? "Vertical drop — End B mirrors End A in plan (drop stays vertical)."
          : "Horizontal run — edit X and Y of each end independently. Floor/Roof shift the elevation."}
      </div>
      {/* v1.98 — Installation side for trays / conduits. Interior (default),
          Exterior (on the OUTSIDE face of the closest wall — e.g. feeders
          to exterior receptacles or lighting), or Through (penetrates the
          wall — e.g. service entry). */}
      <div className="flex items-center gap-2 mt-2">
        <span className="px-1.5 py-0.5 font-bold text-[10px] whitespace-nowrap"
              style={{ background: "#FFA500", color: "#0A0C10" }}>⌂ Install</span>
        <select value={tray.installation_side || "interior"}
                onChange={(e) => onChange({ installation_side: e.target.value })}
                className="bg-black/40 border border-white/10 px-1.5 py-0.5 text-[11px] text-gray-200 focus:border-[#FFA500] outline-none font-mono"
                data-testid={`tray-install-side-${tray.id}`}>
          <option value="interior">Interior (inside enclosure)</option>
          <option value="exterior">Exterior (outside wall face)</option>
          <option value="through">Through (penetrates wall)</option>
        </select>
      </div>
    </div>
  );
}
